import React, { Suspense, lazy, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { User } from '../types';
import { systemApi, type RuntimeContext } from '../services/api';
import { getWebBuildMetadata, type WebBuildMetadata } from '../services/buildMetadata';
import { environmentForHost, showsEnvironmentIndicator, testModeAccess, type AppEnvironment } from '../utils/appEnvironment';
import { useLocalBankIdDebug } from '../context/LocalBankIdDebugContext';
import type { TestModeIntegration } from './TestModePanel';

// Its own chunk, requested only when someone opens the test mode outside
// production: the production build never loads it.
const TestModePanel = lazy(() => import(/* webpackChunkName: "test-mode-panel" */ './TestModePanel'));
type OpenPanel = 'details' | 'testMode' | 'locked' | null;

const COLLAPSED_KEY = 'etappio-env-indicator-collapsed';
const actorPersonaKeys: Record<string, string> = {
  person: 'localBankIdDebug.personas.personSignedIn',
  company: 'localBankIdDebug.personas.company',
  builder: 'localBankIdDebug.personas.builder',
  lawyer: 'localBankIdDebug.personas.lawyer',
  inspector: 'localBankIdDebug.personas.inspector',
  admin: 'persona.admin',
  support: 'persona.support'
};

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

const writeCollapsed = (collapsed: boolean) => {
  try {
    if (collapsed) window.localStorage.setItem(COLLAPSED_KEY, '1');
    else window.localStorage.removeItem(COLLAPSED_KEY);
  } catch {
    // Storage blocked: the choice lasts until reload.
  }
};

type Translate = ReturnType<typeof useTranslation>['t'];

const bankidMode = (context: RuntimeContext) =>
  context.bankid.onboardingProvider === 'simulated' || context.bankid.requiredAssurance === 'simulated'
    ? 'simulated'
    : context.bankid.requiredAssurance === 'production' ? 'production' : 'test';

const providerName = (provider: string) =>
  provider === 'ping' ? 'Ping' : provider === 'adyen' ? 'Adyen' : provider;

const signingMocked = (context: RuntimeContext) => context.signing.provider.startsWith('mock');

const signingName = (provider: string) =>
  provider === 'zigned' ? 'Zigned' : provider === 'scrive' ? 'Scrive' : provider;

const paymentState = (mode: string): TestModeIntegration['state'] =>
  mode === 'mock' ? 'mock' : mode === 'sandbox' ? 'test' : mode === 'live' ? 'live' : 'unknown';

/** The integrations that run mocked, e.g. "BankID simulerad · Adyen mock". */
export function mockedIntegrations(context: RuntimeContext, t: Translate): string[] {
  return [
    ...(bankidMode(context) === 'simulated' ? [t('envIndicator.mocked.bankid')] : []),
    ...(context.payments?.mode === 'mock'
      ? [t('envIndicator.mocked.payments', { provider: providerName(context.payments.provider) })]
      : []),
    ...(signingMocked(context) ? [t('envIndicator.mocked.signing')] : [])
  ];
}

/** The indicator must never take the layout down: anything but the expected
 * shape (an old API, a proxy page, a test double) counts as unavailable. */
const isRuntimeContext = (value: unknown): value is RuntimeContext => {
  if (!value || typeof value !== 'object') return false;
  const context = value as Partial<RuntimeContext>;
  return typeof context.bankid?.onboardingProvider === 'string'
    && typeof context.bankid.requiredAssurance === 'string'
    && typeof context.signing?.provider === 'string'
    && (context.payments === null || (typeof context.payments?.provider === 'string' && typeof context.payments.mode === 'string'));
};

const shortCommit = (commit: string | null | undefined) => (commit ? commit.slice(0, 7) : '–');

/**
 * Colour-coded bar at the very top: which environment this is and who is
 * signed in, so nobody tests or demonstrates in the wrong one. Development is
 * green, staging yellow, production red (internal accounts only; end users in
 * production never get it). It folds to a thin line in the environment's
 * colour; "Detaljer" lists what support needs when something goes wrong.
 * "Testläge" opens the test mode (test actors and integrations) where the
 * server allows it; production access is limited to authorized internal testers.
 */
export default function EnvironmentIndicator({ user, hostname }: {
  user: User | null | undefined;
  /** For tests; defaults to the address the app is served on. */
  hostname?: string;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const host = hostname ?? window.location.hostname;
  const debug = useLocalBankIdDebug();
  const environment: AppEnvironment = debug.enabled && debug.environment === 'testserver' ? 'testserver' : environmentForHost(host);
  const visible = showsEnvironmentIndicator(environment, user, debug.enabled);
  const access = testModeAccess(environment, debug.enabled);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [openPanel, setOpenPanel] = useState<OpenPanel>(null);
  const detailsOpen = openPanel === 'details';
  const [context, setContext] = useState<RuntimeContext | null>(null);
  const [contextFailed, setContextFailed] = useState(false);
  const [webBuild, setWebBuild] = useState<WebBuildMetadata | null>(null);
  const [apiVersion, setApiVersion] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const rootRef = useRef<HTMLDivElement>(null);
  const testModeToggle = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();
  const lockedId = useId();
  // Details and the test mode never stay open together.
  const toggle = (panel: Exclude<OpenPanel, null>) => setOpenPanel(open => (open === panel ? null : panel));
  const toggleTestMode = (event: React.MouseEvent<HTMLButtonElement>) => {
    testModeToggle.current = event.currentTarget;
    toggle(access === 'locked' ? 'locked' : 'testMode');
  };
  const closeTestMode = useCallback(() => setOpenPanel(open => (open === 'testMode' ? null : open)), []);

  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.toggle('env-bar-expanded', !collapsed);
    document.body.classList.toggle('env-bar-collapsed', collapsed);
    return () => document.body.classList.remove('env-bar-expanded', 'env-bar-collapsed');
  }, [visible, collapsed]);

  useEffect(() => {
    // The runtime context needs a session; signed out there is nothing to ask.
    if (!visible || !user?.id) return;
    let active = true;
    systemApi.getRuntimeContext()
      .then(value => {
        if (!active) return;
        if (isRuntimeContext(value)) setContext(value);
        else setContextFailed(true);
      })
      .catch(() => { if (active) setContextFailed(true); });
    return () => { active = false; };
  }, [visible, user?.id]);

  // Versions load the first time the details open; each request on its own,
  // so one finishing does not cancel the other.
  useEffect(() => {
    if (!detailsOpen || webBuild) return;
    let active = true;
    void getWebBuildMetadata().then(value => { if (active) setWebBuild(value); });
    return () => { active = false; };
  }, [detailsOpen, webBuild]);

  useEffect(() => {
    if (!detailsOpen || apiVersion) return;
    let active = true;
    systemApi.getApiVersion()
      .then(value => {
        if (active && typeof value?.version === 'string') setApiVersion(`${value.version} (${shortCommit(value.commit)})`);
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [detailsOpen, apiVersion]);

  // The details and the locked note close on Escape or a click outside; the
  // test mode panel handles that itself.
  const smallPanelOpen = openPanel === 'details' || openPanel === 'locked';
  useEffect(() => {
    if (!smallPanelOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenPanel(null);
    };
    const onPointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpenPanel(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [smallPanelOpen]);

  const changeCollapsed = useCallback((next: boolean) => {
    setCollapsed(next);
    writeCollapsed(next);
    if (next) setOpenPanel(null);
  }, []);

  if (!visible) return null;

  const label = t(`envIndicator.label.${environment}`);
  const personaKey = user?.isAdmin ? 'persona.admin' : user?.isSupport ? 'persona.support'
    : actorPersonaKeys[user?.personaType ?? ''] ?? 'persona.user';
  const persona = user ? t(personaKey) : t('envIndicator.signedOut');
  const signedIn = user?.email ?? '–';
  const mocked = context ? mockedIntegrations(context, t) : [];
  const mockSummary = context
    ? mocked.length ? mocked.join(' · ') : t('envIndicator.mockOff')
    : contextFailed || !user ? t('envIndicator.unavailable') : '…';
  const unavailable = t('envIndicator.unavailable');
  const bankid = context ? t(`envIndicator.bankid.${bankidMode(context)}`) : unavailable;
  const payments = context?.payments
    ? `${providerName(context.payments.provider)} · ${t(`envIndicator.paymentMode.${context.payments.mode}`, context.payments.mode)}`
    : unavailable;
  const signing = context
    ? signingMocked(context) ? t('envIndicator.signingMock') : signingName(context.signing.provider)
    : unavailable;
  const version = t('envIndicator.versionValue', {
    web: webBuild ? `${webBuild.version} (${shortCommit(webBuild.commit)})` : '…',
    api: apiVersion ?? '…'
  });
  const rows: Array<[string, string]> = [
    [t('envIndicator.rows.environment'), t(`envIndicator.name.${environment}`)],
    [t('envIndicator.rows.source'), t(environment === 'testserver' ? 'envIndicator.sourceTestserver' : 'envIndicator.sourceHost', { host })],
    [t('envIndicator.rows.persona'), persona],
    [t('envIndicator.rows.signedIn'), signedIn],
    [t('envIndicator.rows.userId'), user?.id ?? '–'],
    [t('envIndicator.rows.version'), version],
    [t('envIndicator.rows.bankid'), bankid],
    [t('envIndicator.rows.payments'), payments],
    [t('envIndicator.rows.signing'), signing]
  ];

  const integrations: TestModeIntegration[] = context ? [
    { key: 'bankid', name: t('envIndicator.rows.bankid'), value: bankid,
      state: bankidMode(context) === 'simulated' ? 'mock' : bankidMode(context) === 'production' ? 'live' : 'test' },
    { key: 'payments', name: t('envIndicator.rows.payments'), value: payments,
      state: context.payments ? paymentState(context.payments.mode) : 'unknown' },
    { key: 'signing', name: t('envIndicator.rows.signing'), value: signingName(context.signing.provider),
      state: signingMocked(context) ? 'mock' : 'external' }
  ] : [];

  const testModeButton = (compact: boolean) => access === 'locked' ? (
    <button type="button" className={`env-indicator-button is-locked${compact ? ' env-indicator-icon-button' : ''}`}
      aria-expanded={openPanel === 'locked'} aria-controls={lockedId} onClick={toggleTestMode}
      aria-label={compact ? t('envIndicator.testMode.lockedLabel') : undefined} data-testid="env-indicator-test-mode">
      <i className="feather-lock" aria-hidden="true"></i>
      {!compact && <span className="ms-1">{t('envIndicator.testMode.off')}</span>}
    </button>
  ) : (
    <button type="button" className={`env-indicator-button is-test-mode${compact ? ' env-indicator-icon-button' : ''}`}
      aria-expanded={openPanel === 'testMode'} aria-haspopup="dialog" onClick={toggleTestMode}
      aria-label={compact ? t('envIndicator.testMode.openLabel') : undefined} data-testid="env-indicator-test-mode">
      <i className="feather-sliders" aria-hidden="true"></i>
      {!compact && <>
        <span className="ms-1">{t('envIndicator.testMode.open')}</span>
        <i className={`feather-chevron-${openPanel === 'testMode' ? 'up' : 'down'} ms-1`} aria-hidden="true"></i>
      </>}
    </button>
  );

  const copyInfo = async () => {
    const text = [...rows.map(([key, value]) => `${key}: ${value}`), `${t('envIndicator.rows.time')}: ${new Date().toISOString()}`].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
    window.setTimeout(() => setCopyState('idle'), 2500);
  };

  const pill = (
    <span className="env-indicator-pill">
      <i className="feather-monitor" aria-hidden="true"></i>
      {label}
      {user?.syntheticTestActor && <span className="env-indicator-chip" title={t('localBankIdDebug.syntheticSession')}>{t('localBankIdDebug.syntheticLabel')}</span>}
    </span>
  );

  // The bar is fixed; outside the app shell (which pads itself) this spacer
  // gives it its height in the page flow, so it never covers the content.
  const spacer = <div className="env-indicator-spacer" aria-hidden="true"></div>;

  if (collapsed) {
    return (
      <>
        <div className={`env-indicator is-${environment} is-collapsed`} data-testid="env-indicator" data-environment={environment}>
          <button type="button" className="env-indicator-tab" onClick={() => changeCollapsed(false)}
            aria-label={t('envIndicator.expandLabel', { env: label })}>
            {label}
            <i className="feather-chevron-down ms-1" aria-hidden="true"></i>
          </button>
        </div>
        {spacer}
      </>
    );
  }

  return (
    <>
      <div ref={rootRef} className={`env-indicator is-${environment}`} role="region"
        aria-label={t('envIndicator.region')} data-testid="env-indicator" data-environment={environment}>
        {/* Desktop and tablet: one line with the details and fold buttons. */}
        <div className="env-indicator-bar d-none d-md-flex">
          {pill}
          {environment === 'production' && <strong className="env-indicator-warning">{t('envIndicator.realData')}</strong>}
          <span className="env-indicator-item">
            <span className="env-indicator-key">{t('envIndicator.rows.persona')}</span>
            <strong>{persona}</strong>
          </span>
          <span className="env-indicator-item d-none d-lg-inline-flex">
            <span className="env-indicator-key">{t('envIndicator.rows.signedIn')}</span>
            <strong className="text-truncate">{signedIn}</strong>
          </span>
          <span className="env-indicator-item d-none d-xl-inline-flex" data-testid="env-indicator-mock">
            <span className="env-indicator-key">{t('envIndicator.mock')}</span>
            <span className="env-indicator-chip text-truncate">{mockSummary}</span>
          </span>
          <span className="env-indicator-actions">
            {testModeButton(false)}
            <button type="button" className="env-indicator-button" aria-expanded={detailsOpen} aria-controls={panelId}
              onClick={() => toggle('details')}>
              {t('envIndicator.details')}
              <i className={`feather-chevron-${detailsOpen ? 'up' : 'down'} ms-1`} aria-hidden="true"></i>
            </button>
            <button type="button" className="env-indicator-button" onClick={() => changeCollapsed(true)}
              aria-label={t('envIndicator.collapseLabel')}>
              {t('envIndicator.collapse')}
            </button>
          </span>
        </div>

        {/* Phone: the bar opens the details, the button beside it the test mode. */}
        <div className="env-indicator-bar env-indicator-compact-row d-flex d-md-none">
          <button type="button" className="env-indicator-compact" aria-expanded={detailsOpen}
            aria-controls={panelId} onClick={() => toggle('details')}
            aria-label={t(detailsOpen ? 'envIndicator.hideDetails' : 'envIndicator.showDetails', { env: label, persona })}>
            {pill}
            <strong className="text-truncate">{persona}</strong>
            {mocked.length > 0 && <span className="env-indicator-chip">{t('envIndicator.mock')}</span>}
            <i className={`feather-chevron-${detailsOpen ? 'up' : 'down'} ms-auto`} aria-hidden="true"></i>
          </button>
          {testModeButton(true)}
        </div>

        {detailsOpen && (
          <div id={panelId} className="env-indicator-panel" data-testid="env-indicator-details">
            <div className="env-indicator-panel-title">{t('envIndicator.detailsTitle', { env: label })}</div>
            <dl className="env-indicator-rows">
              {rows.map(([key, value]) => (
                <div key={key} className="env-indicator-row">
                  <dt>{key}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            <div className="env-indicator-panel-actions">
              <button type="button" className="btn btn-primary" onClick={() => void copyInfo()}>
                {copyState === 'copied' ? t('envIndicator.copied') : copyState === 'failed' ? t('envIndicator.copyFailed') : t('envIndicator.copy')}
              </button>
              <button type="button" className="btn btn-outline-primary d-md-none" onClick={() => changeCollapsed(true)}>
                {t('envIndicator.collapse')}
              </button>
            </div>
            <span className="visually-hidden" aria-live="polite">
              {copyState === 'copied' ? t('envIndicator.copied') : copyState === 'failed' ? t('envIndicator.copyFailed') : ''}
            </span>
          </div>
        )}

        {openPanel === 'locked' && (
          <div id={lockedId} className="env-indicator-panel env-indicator-locked-note" data-testid="env-indicator-test-mode-locked">
            <i className="feather-lock" aria-hidden="true"></i>
            <p className="mb-0">
              <strong>{t('envIndicator.testMode.lockedTitle')}</strong> {t('envIndicator.testMode.lockedReason')}
            </p>
          </div>
        )}

        {openPanel === 'testMode' && access !== 'locked' && (
          <Suspense fallback={null}>
            <TestModePanel environment={environment} access={access} label={label} integrations={integrations}
              onClose={closeTestMode} returnFocusTo={testModeToggle.current} />
          </Suspense>
        )}
      </div>
      {spacer}
    </>
  );
}
